import { lazy, Suspense, useContext } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Routes, Route, Outlet } from "react-router-dom";

import Header from "core/template/Header";
import Sidebar from "core/template/Sidebar";
import Footer from "core/template/Footer";
import Error from "core/template/Error";
import { authContext } from "configs/context/authContext";
import LoaderComponent from "core/template/LoaderComponent";
import { connectSocket, socket } from "core/utils/socketio";
import RouteGuard from "core/template/routeGuard";
import { MessageProvider } from "configs/context/messageContext";

const DashboardController = lazy(() =>
  import("controllers/dashboardController")
);
const ProfileController = lazy(() => import("controllers/profileController"));

const UserController = lazy(() => import("controllers/userController"));
const DetailUsersController = lazy(() =>
  import("controllers/detailUserController")
);
const ProjectController = lazy(() =>
  import("controllers/projects/projectController")
);
const DetailProjectController = lazy(() =>
  import("controllers/projects/detailProjectController")
);

const MessageController = lazy(() =>
  import("controllers/message/messageController")
);
const InboxMessageController = lazy(() =>
  import("controllers/mail/inboxMailController")
);
const CreateMessageController = lazy(() =>
  import("controllers/mail/createMailController")
);

const DetailMessageController = lazy(() =>
  import("controllers/message/detailMessageController")
);
const MainLayout = () => (
  <>
    <Header />
    <Sidebar />
    <main className="content-wrapper">
      <ErrorBoundary fallback={<Error />}>
        <Outlet />
      </ErrorBoundary>
    </main>
    <Footer />
  </>
);

const MainRoutes = () => {
  const { user } = useContext(authContext);
  // connectSocket(user, "https://api-office.benderaku.id");

  return (
    <Routes>
      {user && (
        <Route element={<MainLayout />}>
          <Route
            path=""
            element={
              <Suspense fallback={<LoaderComponent />}>
                <DashboardController />
              </Suspense>
            }
          />

          <Route
            path="/profile"
            element={
              <Suspense fallback={<LoaderComponent />}>
                <ProfileController />
              </Suspense>
            }
          />
          <Route
            path="/users"
            element={
              <Suspense fallback={<LoaderComponent />}>
                <RouteGuard userRoles={user.modules} routePermission={1}>
                  <UserController />
                </RouteGuard>
              </Suspense>
            }
          />
          <Route
            path="/users/:id"
            element={
              <Suspense fallback={<LoaderComponent />}>
                <RouteGuard userRoles={user.modules} routePermission={1}>
                  <DetailUsersController />
                </RouteGuard>
              </Suspense>
            }
          />

          <Route
            path="/project"
            element={
              <Suspense fallback={<LoaderComponent />}>
                <RouteGuard userRoles={user.modules} routePermission={3}>
                  <ProjectController />
                </RouteGuard>
              </Suspense>
            }
          />

          <Route
            path="/project/:id"
            element={
              <Suspense fallback={<LoaderComponent />}>
                <RouteGuard userRoles={user.modules} routePermission={3}>
                  <DetailProjectController />
                </RouteGuard>
              </Suspense>
            }
          />

          <Route
            path="/message"
            element={
              <Suspense fallback={<LoaderComponent />}>
                <RouteGuard userRoles={user.modules} routePermission={7}>
                  <MessageProvider>
                    <MessageController />
                  </MessageProvider>
                </RouteGuard>
              </Suspense>
            }
          >
            <Route
              path=":id"
              element={
                <Suspense fallback={<LoaderComponent />}>
                  <DetailMessageController />
                </Suspense>
              }
            />
            <Route
              path="create"
              element={
                <Suspense fallback={<LoaderComponent />}>
                  <CreateMessageController />
                </Suspense>
              }
            />
          </Route>

          {/* Catch-all for unknown routes inside authenticated layout */}
          <Route path="*" element={<Error />} />
        </Route>
      )}

      {/* Catch-all for when user is not logged in */}
      {!user && <Route path="*" element={<Error />} />}
    </Routes>
  );
};

export default MainRoutes;
